import React from 'react';
import { AlertCircle, X, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

interface BackendConnectionAlertProps {
  onClose: () => void;
}

export const BackendConnectionAlert: React.FC<BackendConnectionAlertProps> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-blue-50 border-b border-blue-200 px-6 py-4 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-blue-900 mb-1">Running in Demo Mode</h2>
              <p className="text-blue-800 text-sm">
                Backend not accessible - Using sample data for preview
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-blue-600 hover:text-blue-800 p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Status */}
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="mb-2">
              <strong className="text-green-900">✅ App is Working in Demo Mode</strong>
            </div>
            <div className="text-sm space-y-1 text-gray-700">
              <div>🌍 Environment: <span className="font-mono">Figma Make Preview</span></div>
              <div>💾 Data: <span className="font-mono text-green-600">Sample/Mock Data</span></div>
              <div>✨ Features: <span className="text-green-600">All features working with demo data</span></div>
            </div>
          </div>

          {/* What's Working */}
          <div>
            <h3 className="mb-2">✨ What's Available in Demo Mode:</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Profile analytics with sample data (45 visits, 12 shortlists)</li>
              <li>Interest cards (received, sent, accepted) with mock profiles</li>
              <li>Notification system with 2 unread notifications</li>
              <li>Fully functional UI and interactions</li>
              <li>All animations and visual features</li>
            </ul>
          </div>

          {/* Why This Happens */}
          <div>
            <h3 className="mb-2">📌 Why Demo Mode:</h3>
            <p className="text-sm text-gray-700">
              Figma Make's preview environment runs in the cloud and cannot access your local 
              <code className="bg-gray-100 px-1 py-0.5 rounded mx-1">localhost:5001</code> backend server. 
              The app automatically falls back to demo mode with sample data.
            </p>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="mb-3">🚀 To Use Real Data:</h3>
            
            {/* Option 1: ngrok */}
            <div className="border border-blue-200 rounded-lg p-4 mb-3 bg-blue-50">
              <div className="flex items-start gap-2 mb-2">
                <div className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0">1</div>
                <div>
                  <div className="text-blue-900 mb-1">
                    <strong>Use ngrok (Fastest - 2 minutes)</strong> 🚀
                  </div>
                  <p className="text-sm text-blue-800 mb-2">
                    Expose your local backend with a public URL. Perfect for testing!
                  </p>
                </div>
              </div>
              <div className="bg-blue-100 rounded p-3 text-sm font-mono space-y-1 mb-2">
                <div className="text-gray-600"># Terminal 1: Start your backend</div>
                <div>cd backend && npm start</div>
                <div className="text-gray-600 mt-2"># Terminal 2: Start ngrok</div>
                <div>ngrok http 5001</div>
                <div className="text-gray-600 mt-2"># Copy the URL shown (e.g., https://abc123.ngrok.io)</div>
                <div className="text-gray-600"># Then set in Figma Make settings:</div>
                <div className="text-green-600">VITE_BACKEND_URL=https://abc123.ngrok.io</div>
              </div>
              <a
                href="https://ngrok.com/download"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900 underline"
              >
                Download ngrok <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            {/* Option 2: Deploy */}
            <div className="border border-green-200 rounded-lg p-4 mb-3 bg-green-50">
              <div className="flex items-start gap-2 mb-2">
                <div className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0">2</div>
                <div>
                  <div className="text-green-900 mb-1">
                    <strong>Deploy Backend (Recommended for Production)</strong> ⭐
                  </div>
                  <p className="text-sm text-green-800 mb-2">
                    Deploy to a cloud service for a permanent solution.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mb-2">
                <a
                  href="https://render.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-900 underline"
                >
                  Render.com (Free tier) <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href="https://railway.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-900 underline"
                >
                  Railway.app <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href="https://heroku.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-900 underline"
                >
                  Heroku <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="bg-green-100 rounded p-3 text-sm">
                After deployment, set the URL in Figma Make settings:
                <div className="font-mono text-green-700 mt-1">
                  VITE_BACKEND_URL=https://your-backend.onrender.com
                </div>
              </div>
            </div>

            {/* Option 3: Local Dev */}
            <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
              <div className="flex items-start gap-2 mb-2">
                <div className="bg-purple-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0">3</div>
                <div>
                  <div className="text-purple-900 mb-1">
                    <strong>Run Locally (Simplest)</strong> 💻
                  </div>
                  <p className="text-sm text-purple-800 mb-2">
                    Skip Figma Make preview and run the app locally with Vite.
                  </p>
                </div>
              </div>
              <div className="bg-purple-100 rounded p-3 text-sm font-mono space-y-1">
                <div className="text-gray-600"># Terminal 1: Backend</div>
                <div>cd backend && npm start</div>
                <div className="text-gray-600 mt-2"># Terminal 2: Frontend</div>
                <div>npm run dev</div>
                <div className="text-gray-600 mt-2"># Open in browser:</div>
                <div className="text-purple-700">http://localhost:5173</div>
              </div>
            </div>
          </div>

          {/* Test Credentials */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="mb-2">
              <strong className="text-blue-900">📝 Test Credentials (after backend is connected):</strong>
            </div>
            <div className="text-sm space-y-1">
              <div>Email: <code className="bg-blue-100 px-2 py-0.5 rounded">admin@example.com</code></div>
              <div>Password: <code className="bg-blue-100 px-2 py-0.5 rounded">admin123</code></div>
            </div>
          </div>

          {/* Documentation */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="mb-2">
              <strong>📚 Full Documentation:</strong>
            </div>
            <p className="text-sm text-gray-700">
              See <code className="bg-gray-100 px-1 py-0.5 rounded">FIGMA_MAKE_BACKEND_SETUP.md</code> for 
              complete step-by-step instructions.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <Button
            onClick={onClose}
            className="w-full"
            variant="default"
          >
            Got it, I'll set up the backend
          </Button>
        </div>
      </div>
    </div>
  );
};